<template>
  <div
    class="slice-slider"
    ref="handleContainer"
    @pointerdown="onDragStart"
    @pointermove="onDragMove"
    @pointerup="onDragEnd"
    @pointercancel="onDragEnd"
    @contextmenu.prevent
  >
    <!-- Track clair -->
    <div class="slice-slider-track">
      <!-- Track gris dynamique -->
      <div class="slice-slider-track-fill" ref="trackFill"></div>
    </div>

    <!-- Handle -->
    <div
      class="slice-slider-handle"
      ref="handle"
      :style="{
        height: `${handleHeight}px`,
        transform: `translate3d(0, ${handlePosition}px, 0)`,
      }"
    />
  </div>
</template>

<script>
function getYOffsetFromTransform(matStr) {
  if (!matStr || matStr === 'none') return 0;
  const values = matStr.match(/matrix(?:3d)?\((.+)\)/)[1].split(',').map(Number);
  const is3D = matStr.includes('3d');
  return is3D ? values[13] : values[5];
}

export default {
  props: {
    modelValue: { type: Number, default: 0 },
    min: { type: Number, required: true },
    max: { type: Number, required: true },
    step: { type: Number, required: true },
    handleHeight: { type: Number, default: 28 },
  },

  emits: ['update:modelValue'],

  data() {
    return {
      maxHandlePos: 0,
      dragging: false,
      initialHandlePos: 0,
      initialMousePosY: 0,
      yOffset: 0,
      resizeObserver: null,
    };
  },

  computed: {
    handlePosition() {
      const range = this.max - this.min || 1;
      const pos = this.maxHandlePos * ((this.modelValue - this.min) / range);
      return this.dragging ? this.draggingHandlePos : pos;
    },
    draggingHandlePos() {
      return Math.min(Math.max(0, this.initialHandlePos + this.yOffset), this.maxHandlePos);
    },
  },

  mounted() {
    this.updateMaxHandlePos();

    this.resizeObserver = new ResizeObserver(() => this.updateMaxHandlePos());
    this.resizeObserver.observe(this.$refs.handleContainer);

    this.updateTrackFill();
  },

  beforeUnmount() {
    if (this.resizeObserver) this.resizeObserver.disconnect();
  },

  watch: {
    handlePosition() {
      this.updateTrackFill();
    },
    modelValue() {
      this.updateTrackFill();
    }
  },

  methods: {
    updateMaxHandlePos() {
      this.maxHandlePos = this.$refs.handleContainer.clientHeight - this.handleHeight;
      this.updateTrackFill();
    },

    updateTrackFill() {
      if (this.$refs.trackFill) {
        this.$refs.trackFill.style.height = `${this.handlePosition + this.handleHeight / 2}px`;
      }
    },

    onDragStart(ev) {
      ev.preventDefault();
      this.dragging = true;
      this.initialMousePosY = ev.pageY;

      if (ev.target === this.$refs.handle) {
        const handleStyles = window.getComputedStyle(this.$refs.handle);
        this.initialHandlePos = getYOffsetFromTransform(handleStyles.transform);
      } else {
        const { y } = this.$refs.handleContainer.getBoundingClientRect();
        this.initialHandlePos = Math.max(
          0,
          Math.min(this.maxHandlePos, ev.pageY - y - this.handleHeight / 2)
        );
        this.$emit('update:modelValue', this.getNearestSlice(this.initialHandlePos));
      }

      this.yOffset = 0;
      this.$refs.handleContainer.setPointerCapture(ev.pointerId);
    },

    onDragMove(ev) {
      if (!this.$refs.handleContainer.hasPointerCapture(ev.pointerId)) return;
      ev.preventDefault();
      this.yOffset = ev.pageY - this.initialMousePosY;

      const slice = this.getNearestSlice(this.handlePosition);
      this.$emit('update:modelValue', slice);
    },

    onDragEnd(ev) {
      if (!this.$refs.handleContainer.hasPointerCapture(ev.pointerId)) return;
      ev.preventDefault();
      this.$refs.handleContainer.releasePointerCapture(ev.pointerId);
      this.dragging = false;

      const slice = this.getNearestSlice(this.handlePosition);
      this.$emit('update:modelValue', slice);
    },

    getNearestSlice(pos) {
      const sliceEstimate = pos / this.maxHandlePos;
      const frac = sliceEstimate * (this.max - this.min) + this.min;
      return Math.round(frac / this.step) * this.step;
    }
  },
};
</script>

<style scoped>
.slice-slider {
  touch-action: none;
  width: 60px;
  height: 100%;
  display: flex;
  justify-content: center;
  position: relative;

    margin-left: 2px;
}

.slice-slider-handle {
  position: absolute;
  width: 24px;
  aspect-ratio: 1;
  background: white;
  border-radius: 50%;
  border: 2px solid #e0e0e0;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.2);
  cursor: grab;
  z-index: 10;
  left: 50%;
  margin-left: -12px;

}

.slice-slider-handle:active {
  cursor: grabbing;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.3);
}

.slice-slider-track {
  position: absolute;
  top: 0;
  bottom: 0;
  left: 50%;
  transform: translateX(-50%);
  width: 8px;
  background: #e8e8f0;
  border-radius: 4px;
  box-shadow: inset 0 1px 3px rgba(0,0,0,0.1);
}

.slice-slider-track-fill {
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  background: #6b7280;
  border-radius: 4px;
  height: 0;
}
</style>
